import type { AgentMessageToolCalls } from '@aline/common';
import { Apollo } from 'src/gql/generated';

type AgentMessageStatus = Apollo.AgentMessageStatus;

/**
 * Chat User Message
 */
export type ChatUserMessage = {
  id: string;
  role: Apollo.AgentMessageRole;
  status: AgentMessageStatus;
  text: string;
};

/**
 * Chat Assistant Message Suggestion Status
 */
enum ClientSuggestionStatus {
  DocumentNotInView = 'DOCUMENT_NOT_IN_VIEW',
  NotAlineDoc = 'NOT_ALINE_DOC',
  TextChanged = 'TEXT_CHANGED',
}
export type SuggestionStatus = Apollo.MessageSuggestionStatus | ClientSuggestionStatus;

export const SuggestionStatus = {
  ...Apollo.MessageSuggestionStatus,
  ...ClientSuggestionStatus,
};

export type ChatMessageSuggestionStatus = {
  status: Apollo.MessageSuggestionStatus;
  userId: UUID;
};

/**
 * Durable OCR wait on assistant message.data — mirrors server MessageData.
 * Banner UI only cares about phase === 'prePlanning'.
 */
export type DocumentExtractionPause =
  | { fileVersionIds: string[]; phase: 'prePlanning' }
  | {
      pauses: Array<{
        documentId: string;
        fileVersionId: string;
        nodeId: number;
        toolCallId: string;
        toolInput: Record<string, unknown>;
        toolName: 'queryDocument' | 'readDocument';
      }>;
      phase: 'midTurn';
    };

/**
 * Chat Assistant Message
 */
export type ChatAssistantMessage = {
  createdAt: string;
  data: {
    documentExtractionPause?: DocumentExtractionPause;
    suggestionStates?: Record<string, ChatMessageSuggestionStatus>;
    toolCalls?: AgentMessageToolCalls;
    uiParts?: unknown[];
  };
  id: string;
  role: Apollo.AgentMessageRole;
  status: AgentMessageStatus;
  text: string;
  feedback?: Apollo.AgentMessageFeedback | null;
  /**
   * Raw planState JSON for harness-authored messages; null for V1. Validated
   * against PlanStateSchema at render time inside the PlanTree component, so
   * we keep the loose Record<string, any> at the GraphQL boundary.
   */
  planState?: Record<string, unknown> | null;
};

/**
 * Combination of User and Assistant Message
 */
export type ChatMessage = ChatUserMessage | ChatAssistantMessage;
